import React from 'react';
import { OrderFormData } from './types';
import {
    SectionContainer,
    AddressGrid,
    AddressCard,
    SummaryRow,
    StatusBadge,
    CompactPreview
} from './styles';

interface ShippingSummaryProps {
    formData: OrderFormData;
}

export const ShippingSummary: React.FC<ShippingSummaryProps> = ({ formData }) => {
    const getStatus = (value: string | number | undefined | null) => {
        return value ? 'confirmed' : 'missing';
    };

    const totalPercentage = formData.remittanceTranches.reduce((sum, tranche) => sum + tranche.percentage, 0);
    const percentageError = Math.abs(totalPercentage - 100) > 0.01;

    // Validate remittance schedule
    const getValidationErrors = () => {
        const errors: string[] = [];

        formData.remittanceTranches.forEach((tranche, index) => {
            if (!tranche.milestone) {
                errors.push(`Tranche ${index + 1}: Milestone not selected`);
            }
            if (!tranche.dueDate) {
                errors.push(`Tranche ${index + 1}: Due date missing`);
            }
            if (index > 0) {
                const prevDate = new Date(formData.remittanceTranches[index - 1].dueDate);
                const currentDate = new Date(tranche.dueDate);
                if (currentDate <= prevDate) {
                    errors.push(`Tranche ${index + 1}: Date must be after previous date`);
                }
            }
        });

        if (percentageError) {
            errors.push(`Total percentage must equal 100% (currently ${totalPercentage.toFixed(2)}%)`);
        }

        return errors;
    };

    const validationErrors = getValidationErrors();
    const hasErrors = validationErrors.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <SectionContainer>
                <h3>Parties & Addresses</h3>
                <AddressGrid>
                    <AddressCard style={{ display: 'flex', flexDirection: 'column', gap: '1rem', gridColumn: 'span 1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Pickup / Vendor</h4>
                            <StatusBadge $status={getStatus(formData.VendorName)}>
                                {formData.VendorName ? 'Confirmed' : 'Action Required'}
                            </StatusBadge>
                        </div>
                        <CompactPreview>
                            <strong>{formData.VendorName || 'Name Missing'}</strong>
                            <p>{formData.pickupAddr1}</p>
                            <p>{formData.pickupCity}, {formData.pickupCountry}</p>
                            <p>{formData.VendorEmail}</p>
                        </CompactPreview>
                    </AddressCard>

                    <AddressCard style={{ display: 'flex', flexDirection: 'column', gap: '1rem', gridColumn: 'span 1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Delivery / Customer</h4>
                            <StatusBadge $status={getStatus(formData.CustomerName)}>
                                {formData.CustomerName ? 'Confirmed' : 'Action Required'}
                            </StatusBadge>
                        </div>
                        <CompactPreview>
                            <strong>{formData.CustomerName || 'Name Missing'}</strong>
                            <p>{formData.deliveryAddr1}</p>
                            <p>{formData.deliveryCity}, {formData.deliveryCountry}</p>
                            <p>{formData.CustomerEmail}</p>
                        </CompactPreview>
                    </AddressCard>
                </AddressGrid>
            </SectionContainer>

            <SectionContainer>
                <h3>Shipment Details</h3>
                <div style={{ backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '8px' }}>
                    <SummaryRow>
                        <span>Service Speed</span>
                        <strong>{formData.priorityLevel?.toUpperCase()}</strong>
                    </SummaryRow>
                    <SummaryRow>
                        <span>Total Items</span>
                        <strong>{formData.items.length} items</strong>
                    </SummaryRow>
                    <SummaryRow>
                        <span>Incoterms</span>
                        <strong>{formData.incoterms || 'Not Specified'}</strong>
                    </SummaryRow>
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontWeight: 600, color: '#334155' }}>Remittance Schedule</span>
                            <strong style={{ color: percentageError ? '#dc2626' : '#16a34a' }}>{totalPercentage.toFixed(2)}%</strong>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {formData.remittanceTranches.map((tranche, index) => {
                                const amount = ((formData.total || 0) * tranche.percentage) / 100;
                                return (
                                    <div key={`${index}-${tranche.milestone}`} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                                        <SummaryRow>
                                            <span>Tranche {index + 1}</span>
                                            <strong>{tranche.percentage}%</strong>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <span>Amount</span>
                                            <strong>{formData.currency} {amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <span>Due Date</span>
                                            <strong>{tranche.dueDate || 'Not set'}</strong>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <span>Milestone</span>
                                            <strong>{tranche.milestone || 'Not set'}</strong>
                                        </SummaryRow>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Validation Warnings */}
                    {hasErrors && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '12px',
                            background: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            color: '#991b1b',
                            fontSize: '13px',
                        }}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                ⚠️ Remittance Schedule Issues
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {validationErrors.map((error, idx) => (
                                    <li key={idx}>{error}</li>
                                ))}
                            </ul>
                            <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.9 }}>
                                Please fix these issues in the Delivery step before creating the order.
                            </div>
                        </div>
                    )}

                    <SummaryRow>
                        <span>Insurance</span>
                        <StatusBadge $status={formData.insuranceRequired ? 'confirmed' : 'review'}>
                            {formData.insuranceRequired ? 'Included' : 'Not Requested'}
                        </StatusBadge>
                    </SummaryRow>
                </div>
            </SectionContainer>
        </div>
    );
};
